import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export interface ProgressPhase {
  id: string;
  phase: 'file-processing' | 'ai-generation' | 'completed' | 'error' | 'idle';
  processed: number;
  total: number;
  percentage: number;
  status: 'idle' | 'pending' | 'processing' | 'completed' | 'error';
  currentAction: string;
  isProcessing: boolean;
  error?: string;
  source: 'file-processing' | 'ai-generation' | 'database' | 'user-initiated' | 'none';
  lastUpdated: Date;
  isCompleted: boolean;
  stage: string;
}

interface GlobalProgressState {
  // Active progress tracking by collection ID
  activeProgress: Record<string, ProgressPhase>;
  
  // Methods
  setProgress: (collectionId: string, progress: Partial<ProgressPhase>) => void;
  clearProgress: (collectionId: string) => void;
  getProgress: (collectionId: string) => ProgressPhase | null;
  
  // Phase transition management
  startPhaseTransition: (collectionId: string, fromPhase: string, toPhase: string) => void;
  completePhaseTransition: (collectionId: string) => void;
  
  // Force visibility for navigation resilience
  forceShowProgress: (collectionId: string, duration?: number) => void;
  
  // Check if should show progress
  shouldShowProgress: (collectionId: string) => boolean;
}

const defaultProgress: ProgressPhase = {
  id: '',
  phase: 'idle',
  processed: 0,
  total: 0,
  percentage: 0,
  status: 'pending',
  currentAction: 'Ready',
  isProcessing: false,
  source: 'none',
  lastUpdated: new Date(),
  isCompleted: false,
  stage: 'pending'
};

export const useGlobalProgressStore = create<GlobalProgressState>()(
  subscribeWithSelector((set, get) => ({
    activeProgress: {},

    setProgress: (collectionId: string, progress: Partial<ProgressPhase>) => {
      set((state) => {
        const existing = state.activeProgress[collectionId] || { ...defaultProgress, id: collectionId };
        const updated = {
          ...existing,
          ...progress,
          lastUpdated: new Date(),
          percentage: progress.total && progress.total > 0 
            ? Math.round(((progress.processed || existing.processed) / progress.total) * 100)
            : existing.percentage
        };

        // Auto-complete logic
        if (updated.percentage >= 100 && updated.total > 0 && updated.processed >= updated.total) {
          updated.isCompleted = true;
          updated.status = 'completed';
          updated.phase = 'completed';
          updated.isProcessing = false;
        }

        return {
          activeProgress: {
            ...state.activeProgress,
            [collectionId]: updated
          }
        };
      });
    },

    clearProgress: (collectionId: string) => {
      set((state) => {
        const { [collectionId]: removed, ...rest } = state.activeProgress;
        return { activeProgress: rest };
      });
    },

    getProgress: (collectionId: string) => {
      return get().activeProgress[collectionId] || null;
    },

    startPhaseTransition: (collectionId: string, fromPhase: string, toPhase: string) => {
      console.log(`🔄 Phase transition: ${fromPhase} → ${toPhase} for collection ${collectionId}`);
      
      set((state) => {
        const existing = state.activeProgress[collectionId];
        if (!existing) return state;

        return {
          activeProgress: {
            ...state.activeProgress,
            [collectionId]: {
              ...existing,
              phase: toPhase as any,
              isProcessing: true,
              currentAction: `Transitioning to ${toPhase}...`,
              lastUpdated: new Date()
            }
          }
        };
      });
    },

    completePhaseTransition: (collectionId: string) => {
      set((state) => {
        const existing = state.activeProgress[collectionId];
        if (!existing) return state;

        return {
          activeProgress: {
            ...state.activeProgress,
            [collectionId]: {
              ...existing,
              currentAction: existing.phase === 'ai-generation' 
                ? 'Generating descriptions...' 
                : existing.currentAction,
              lastUpdated: new Date()
            }
          }
        };
      });
    },

    forceShowProgress: (collectionId: string, duration = 30000) => {
      set((state) => {
        const existing = state.activeProgress[collectionId] || { ...defaultProgress, id: collectionId };
        
        const updated = {
          ...existing,
          isProcessing: true,
          currentAction: existing.currentAction || 'Processing...',
          lastUpdated: new Date()
        };

        // Auto-clear after duration
        setTimeout(() => {
          const current = get().activeProgress[collectionId];
          if (current && !current.isProcessing) {
            get().clearProgress(collectionId);
          }
        }, duration);

        return {
          activeProgress: {
            ...state.activeProgress,
            [collectionId]: updated
          }
        };
      });
    },

    shouldShowProgress: (collectionId: string) => {
      const progress = get().activeProgress[collectionId];
      if (!progress) return false;

      return (
        progress.isProcessing ||
        (progress.total > 0 && !progress.isCompleted && progress.percentage < 100) ||
        (progress.status === 'processing') ||
        (progress.phase !== 'idle' && progress.phase !== 'completed')
      );
    }
  }))
);